import { GoogleGenAI } from '@google/genai';

// --- TYPES ---
export const Direction = {
  N: 1,
  E: 2,
  S: 4,
  W: 8,
};

export const ItemType = {
  Start: 'start',
  Exit: 'exit',
  Health: 'health',
  Score: 'score',
  Hazard: 'hazard',
  Weapon: 'weapon',
  Empty: 'empty',
  // Elemental / Theme Items
  IceCube: 'ice_cube',
  Fire: 'fire',
  CO2: 'co2',
  Fumes: 'fumes',
  Antivenom: 'antivenom',
  Robber: 'robber',
  // Specific Hazards
  Snake: 'snake',
  Quicksand: 'quicksand',
  Bees: 'bees',
  Meteor: 'meteor',
  Aliens: 'aliens',
  Bear: 'bear',
  SolarFlare: 'solar_flare',
  Earthquake: 'earthquake',
  Dog: 'dog',
  BigCat: 'big_cat',
  Gorilla: 'gorilla',
  Sunburn: 'sunburn',
  Storm: 'storm',
  GasLeak: 'gas_leak',
  FallingIce: 'falling_ice',
  Pothole: 'pothole',
  FastCar: 'fast_car',
  // New Items
  Water: 'water',
  Bandage: 'bandage',
  Herb: 'herb',
  Honey: 'honey',
  Rope: 'rope',
  Stick: 'stick',
  Teamwork: 'teamwork',
  ShieldGenerator: 'shield_generator',
  EvasiveManeuver: 'evasive_maneuver',
  NegotiationItem: 'negotiation_item',
  RadiationShield: 'radiation_shield',
  OxygenTank: 'oxygen_tank',
  OxygenMask: 'oxygen_mask',
  Root: 'root',
  Trap: 'trap',
  Shelter: 'shelter',
  PharmacyPack: 'pharmacy_pack',
  Sunscreen: 'sunscreen',
  LifeJacket: 'life_jacket',
  Ventilation: 'ventilation',
  Helmet: 'helmet',
  Shield: 'shield',
  Money: 'money',
  ReflexBoost: 'reflex_boost',
  Dodge: 'dodge',
  HealthBerries: 'health_berries',
  FlowerPollen: 'flower_pollen',
  GameMeat: 'game_meat',
  Syringe: 'syringe',
  PoisonBerries: 'poison_berries',
  GasMask: 'gas_mask',
} as const;

export type ItemTypeValues = typeof ItemType[keyof typeof ItemType];

const HAZARD_REMEDIES: Partial<Record<ItemTypeValues, ItemTypeValues[]>> = {
  [ItemType.Fire]: [ItemType.Water, ItemType.IceCube, ItemType.CO2],
  [ItemType.Fumes]: [ItemType.OxygenMask, ItemType.GasMask, ItemType.Herb],
  [ItemType.Robber]: [ItemType.Weapon, ItemType.Shield],
  [ItemType.Snake]: [ItemType.Antivenom, ItemType.Bandage, ItemType.Weapon],
  [ItemType.Quicksand]: [ItemType.Rope, ItemType.Stick, ItemType.Teamwork],
  [ItemType.Bees]: [ItemType.Herb, ItemType.Honey],
  [ItemType.Meteor]: [ItemType.ShieldGenerator, ItemType.EvasiveManeuver, ItemType.LifeJacket],
  [ItemType.Aliens]: [ItemType.NegotiationItem, ItemType.Teamwork, ItemType.Weapon],
  [ItemType.Bear]: [ItemType.Trap, ItemType.GameMeat, ItemType.Weapon],
  [ItemType.PoisonBerries]: [ItemType.Herb, ItemType.Syringe, ItemType.Health],
  [ItemType.SolarFlare]: [ItemType.RadiationShield, ItemType.OxygenTank],
  [ItemType.Earthquake]: [ItemType.Shelter, ItemType.Rope, ItemType.Teamwork],
  [ItemType.Dog]: [ItemType.Bandage, ItemType.Health, ItemType.PharmacyPack],
  [ItemType.BigCat]: [ItemType.Weapon, ItemType.Health, ItemType.Honey],
  [ItemType.Gorilla]: [ItemType.HealthBerries, ItemType.Herb, ItemType.Root, ItemType.FlowerPollen, ItemType.Honey],
  [ItemType.Sunburn]: [ItemType.Sunscreen, ItemType.Water],
  [ItemType.Storm]: [ItemType.LifeJacket, ItemType.Rope, ItemType.OxygenTank],
  [ItemType.GasLeak]: [ItemType.OxygenMask, ItemType.GasMask, ItemType.Ventilation, ItemType.Health],
  [ItemType.FallingIce]: [ItemType.Helmet, ItemType.Shield],
  [ItemType.Pothole]: [ItemType.Money, ItemType.Health],
  [ItemType.FastCar]: [ItemType.ReflexBoost, ItemType.Dodge],
};

export const HAZARD_TYPES: ItemTypeValues[] = [
  ItemType.Fire, ItemType.Fumes, ItemType.Robber, ItemType.Snake,
  ItemType.Quicksand, ItemType.Bees, ItemType.Meteor, ItemType.Aliens,
  ItemType.Bear, ItemType.PoisonBerries, ItemType.SolarFlare,
  ItemType.Earthquake, ItemType.Dog, ItemType.BigCat, ItemType.Gorilla,
  ItemType.Sunburn, ItemType.Storm, ItemType.GasLeak, ItemType.FallingIce,
  ItemType.Pothole, ItemType.FastCar,
];

export const HAZARD_ENCOUNTER_IDS: Partial<Record<ItemTypeValues, string>> = {
  [ItemType.Fire]: 'forest_fire',
  [ItemType.Fumes]: 'toxic_fumes',
  [ItemType.Robber]: 'robber',
  [ItemType.Snake]: 'snake_bite',
  [ItemType.Quicksand]: 'quicksand',
  [ItemType.Bees]: 'bee_sting',
  [ItemType.Meteor]: 'meteor_storm',
  [ItemType.Aliens]: 'violent_alien',
  [ItemType.Bear]: 'hostile_wildlife',
  [ItemType.PoisonBerries]: 'poison_berries',
  [ItemType.SolarFlare]: 'solar_flare',
  [ItemType.Earthquake]: 'earthquake',
  [ItemType.Dog]: 'dog_bite',
  [ItemType.BigCat]: 'leopard_attack',
  [ItemType.Gorilla]: 'gorilla_attack',
  [ItemType.Sunburn]: 'sunburn',
  [ItemType.Storm]: 'storm',
  [ItemType.GasLeak]: 'gas_leak',
  [ItemType.FallingIce]: 'falling_ice',
  [ItemType.Pothole]: 'pothole',
  [ItemType.FastCar]: 'fast_car',
};

const countItems = (items: ItemTypeValues[]): Map<ItemTypeValues, number> => {
  const counts = new Map<ItemTypeValues, number>();
  items.forEach(item => counts.set(item, (counts.get(item) || 0) + 1));
  return counts;
};

const pickLeastRepresented = (
  choices: ItemTypeValues[],
  existingCounts: Map<ItemTypeValues, number>,
  placedCounts: Map<ItemTypeValues, number>,
): ItemTypeValues => {
  const minimum = Math.min(...choices.map(item => (existingCounts.get(item) || 0) + (placedCounts.get(item) || 0)));
  const leastRepresented = choices.filter(item => (existingCounts.get(item) || 0) + (placedCounts.get(item) || 0) === minimum);
  return leastRepresented[Math.floor(Math.random() * leastRepresented.length)];
};

export const selectDiverseItems = (
  choices: ItemTypeValues[],
  count: number,
  existingItems: ItemTypeValues[] = [],
): ItemTypeValues[] => {
  const existingCounts = countItems(existingItems);
  const selectedCounts = new Map<ItemTypeValues, number>();
  const selected: ItemTypeValues[] = [];

  while (selected.length < count && choices.length > 0) {
    const item = pickLeastRepresented(choices, existingCounts, selectedCounts);
    selected.push(item);
    selectedCounts.set(item, (selectedCounts.get(item) || 0) + 1);
  }

  return selected;
};

export interface Cell {
  x: number;
  y: number;
  walls: number;
  item: ItemTypeValues;
  seen: boolean;
  visible: boolean;
  valid: boolean;
}

export type Theme = 'dungeon' | 'orchard' | 'farm' | 'vacation' | 'forest' | 'ocean' | 'cartoon' | 'galaxy';

export const LEVEL_CONFIG: Record<number, { w: number; h: number; shape: string; theme: Theme }> = {
  1: { w: 6,  h: 6,  shape: 'rectangle', theme: 'dungeon' },
  2: { w: 8,  h: 8,  shape: 'rectangle', theme: 'orchard' },
  3: { w: 10, h: 10, shape: 'circle',    theme: 'farm' },
  4: { w: 12, h: 12, shape: 'diamond',   theme: 'vacation' },
  5: { w: 12, h: 12, shape: 'pineapple', theme: 'forest' },
  6: { w: 14, h: 14, shape: 'circle',    theme: 'ocean' },
  7: { w: 14, h: 14, shape: 'diamond',   theme: 'cartoon' },
  8: { w: 16, h: 16, shape: 'pineapple', theme: 'galaxy' },
};

// --- MAZE GENERATOR ---
const DX = { [Direction.E]: 1, [Direction.W]: -1, [Direction.N]: 0, [Direction.S]: 0 };
const DY = { [Direction.E]: 0, [Direction.W]: 0, [Direction.N]: -1, [Direction.S]: 1 };
const OPPOSITE = { [Direction.E]: Direction.W, [Direction.W]: Direction.E, [Direction.N]: Direction.S, [Direction.S]: Direction.N };

const isCellInShape = (x: number, y: number, w: number, h: number, shape: string): boolean => {
  const cx = w / 2;
  const cy = h / 2;
  const nx = (x - cx) / (w / 2); // Normalized -1 to 1
  const ny = (y - cy) / (h / 2);

  switch (shape) {
    case 'circle':
      return (nx * nx + ny * ny) <= 1;
    case 'diamond':
      return (Math.abs(nx) + Math.abs(ny)) <= 1;
    case 'pineapple':
      // Oval body
      if (ny > -0.5) return (nx * nx + ny * ny) <= 0.8;
      // Leaves on top
      return (Math.abs(nx) < 0.3 && ny > -0.9);
    case 'rectangle':
    default:
      return true;
  }
};

export const generateMaze = (width: number, height: number, shape: string = 'rectangle', difficulty: 'easy' | 'hard' | 'tough_genius' = 'easy', level: number = 1, playerInventory: ItemTypeValues[] = []): { grid: Cell[][], startPos: { x: number, y: number } } => {
  const grid: Cell[][] = [];
  const visited: boolean[][] = [];

  // 1. Initialize Grid
  for (let y = 0; y < height; y++) {
    const row: Cell[] = [];
    const visitedRow: boolean[] = [];
    for (let x = 0; x < width; x++) {
      const isValid = isCellInShape(x, y, width, height, shape);
      row.push({
        x, y,
        walls: 15, // All walls
        item: ItemType.Empty,
        seen: false,
        visible: false,
        valid: isValid
      });
      // Mark invalid cells as visited so the generator doesn't try to go there
      visitedRow.push(!isValid);
    }
    grid.push(row);
    visited.push(visitedRow);
  }

  // 2. Recursive Backtracker (Guarantees perfect connectivity)
  const genStartX = Math.floor(width / 2);
  const genStartY = Math.floor(height / 2);
  
  const stack: { x: number, y: number }[] = [];
  // Ensure start is valid (it should be for our shapes, but safety first)
  if (grid[genStartY][genStartX].valid) {
    visited[genStartY][genStartX] = true;
    stack.push({ x: genStartX, y: genStartY });
  }

  while (stack.length > 0) {
    const current = stack[stack.length - 1];
    const neighbors = [Direction.N, Direction.S, Direction.E, Direction.W]
      .map(dir => ({ dir, x: current.x + DX[dir], y: current.y + DY[dir] }))
      .filter(n => 
        n.x >= 0 && n.x < width && n.y >= 0 && n.y < height && 
        !visited[n.y][n.x]
      );

    if (neighbors.length > 0) {
      const next = neighbors[Math.floor(Math.random() * neighbors.length)];
      
      // Remove walls
      grid[current.y][current.x].walls &= ~next.dir;
      grid[next.y][next.x].walls &= ~OPPOSITE[next.dir];

      visited[next.y][next.x] = true;
      stack.push({ x: next.x, y: next.y });
    } else {
      stack.pop();
    }
  }

  // POST-PROCESS: Connect any isolated valid cells (walls === 15 = never visited)
  // These arise when a valid cell only borders invalid cells, so the backtracker skips it.
  // Fix: find the nearest visited neighbour and carve a passage to it.
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const cell = grid[y][x];
      if (!cell.valid || cell.walls !== 15) continue; // skip: invalid or already carved

      // Search neighbours in all 4 directions for a visited valid cell to connect to
      const dirs = [Direction.N, Direction.S, Direction.E, Direction.W];
      let connected = false;
      for (const dir of dirs) {
        const nx = x + DX[dir];
        const ny = y + DY[dir];
        if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
        const neighbour = grid[ny][nx];
        if (!neighbour.valid) continue; // don't connect through invalid cells
        if (neighbour.walls === 15) continue; // neighbour is also unvisited, skip for now
        // Carve passage between isolated cell and this visited neighbour
        cell.walls &= ~dir;
        neighbour.walls &= ~OPPOSITE[dir];
        connected = true;
        break;
      }

      // If still isolated (all valid neighbours also unvisited), mark invalid so it's hidden
      if (!connected) {
        cell.valid = false;
      }
    }
  }

  // 1. Identify all carved cells (reachable)
  const carvedCells = grid.flat().filter(c => c.valid && c.walls !== 15);
  
  // 2. Pick Random Player Start
  const playerStartCell = carvedCells[Math.floor(Math.random() * carvedCells.length)];
  const startX = playerStartCell.x;
  const startY = playerStartCell.y;
  grid[startY][startX].item = ItemType.Start;

  // 3. BFS from NEW Start to find distances
  const dist = new Map<string, number>();
  const q: {x: number, y: number, d: number}[] = [{x: startX, y: startY, d: 0}];
  dist.set(`${startX},${startY}`, 0);
  let maxDist = 0;

  while (q.length > 0) {
    const {x, y, d} = q.shift()!;
    maxDist = Math.max(maxDist, d);

    [Direction.N, Direction.S, Direction.E, Direction.W].forEach(dir => {
      if (!(grid[y][x].walls & dir)) { // If no wall
        const nx = x + DX[dir];
        const ny = y + DY[dir];
        if (!dist.has(`${nx},${ny}`)) {
          dist.set(`${nx},${ny}`, d + 1);
          q.push({x: nx, y: ny, d: d + 1});
        }
      }
    });
  }

  // 4. Find candidates for exit
  // Level 1 starts at 0.25 (short path = easy to find exit), scaling up to 0.70 by level 8.
  // This targets ~80% completion rate at level 1 and increasing challenge each level.
  const minDistanceRatio = Math.min(0.25 + (level - 1) * 0.065, 0.70);
  const exitCandidates = grid.flat().filter(c => {
    if (!c.valid || (c.x === startX && c.y === startY)) return false;

    const d = dist.get(`${c.x},${c.y}`) || 0;
    if (d < maxDist * minDistanceRatio) return false;

    // Check if edge
    const isEdge = [Direction.N, Direction.S, Direction.E, Direction.W].some(dir => {
      const nx = c.x + DX[dir];
      const ny = c.y + DY[dir];
      return nx < 0 || nx >= width || ny < 0 || ny >= height || !grid[ny][nx].valid;
    });

    return isEdge;
  });

  if (exitCandidates.length > 0) {
    const exit = exitCandidates[Math.floor(Math.random() * exitCandidates.length)];
    exit.item = ItemType.Exit;
  } else {
    const fallback = grid.flat().filter(c => c.valid && c.walls !== 15 && c.item === ItemType.Empty && !(c.x === startX && c.y === startY)).pop();
    if (fallback) fallback.item = ItemType.Exit;
  }

  // 5. Place Items (exclude Start and Exit)
  const validItemCells = grid.flat().filter(c => c.valid && c.walls !== 15 && c.item === ItemType.Empty);
  const placedItems: {x: number, y: number}[] = [];
  
  // Add Start and Exit to placed items to avoid crowding them
  placedItems.push({x: startX, y: startY});
  const exitCell = grid.flat().find(c => c.item === ItemType.Exit);
  if (exitCell) placedItems.push({x: exitCell.x, y: exitCell.y});

  const isSafeDistance = (c: Cell) => {
    return placedItems.every(p => (Math.abs(c.x - p.x) + Math.abs(c.y - p.y)) >= 3);
  };

  const pickSafeCell = () => {
    // Filter valid cells by distance
    const safeCells = validItemCells.filter(c => isSafeDistance(c));
    if (safeCells.length === 0) return null;
    
    const idx = Math.floor(Math.random() * safeCells.length);
    const cell = safeCells[idx];
    
    // Remove from validItemCells (find index in original array)
    const originalIdx = validItemCells.indexOf(cell);
    if (originalIdx > -1) validItemCells.splice(originalIdx, 1);
    
    placedItems.push({x: cell.x, y: cell.y});
    return cell;
  };

  const placedHazardCells: Array<{ x: number; y: number }> = [];
  const pickSafeHazardCell = () => {
    const safeCells = validItemCells.filter(c =>
      isSafeDistance(c) &&
      placedHazardCells.every(p => (Math.abs(c.x - p.x) + Math.abs(c.y - p.y)) >= 4)
    );
    if (safeCells.length === 0) return null;

    const cell = safeCells[Math.floor(Math.random() * safeCells.length)];
    validItemCells.splice(validItemCells.indexOf(cell), 1);
    placedItems.push({ x: cell.x, y: cell.y });
    placedHazardCells.push({ x: cell.x, y: cell.y });
    return cell;
  };
  
  // Difficulty + Level Scaling
  // Base hazard rate rises from 3% (level 1) to 10% (level 8); difficulty multiplies on top.
  const levelHazardBase = 0.03 + (level - 1) * 0.01;
  let hazardCount = Math.floor(validItemCells.length * levelHazardBase);
  if (difficulty === 'hard') hazardCount = Math.floor(validItemCells.length * (levelHazardBase + 0.05));
  if (difficulty === 'tough_genius') hazardCount = Math.floor(validItemCells.length * (levelHazardBase + 0.10));

  // Place hazards and remember their types so every generated maze can also
  // contain at least one matching mitigation item.
  const placedHazards: ItemTypeValues[] = [];
  const hazardCounts = new Map<ItemTypeValues, number>();
  for (let i = 0; i < hazardCount; i++) {
    const cell = pickSafeHazardCell();
    if (!cell) break;

    // Use every hazard once before repeating any type.
    cell.item = pickLeastRepresented(HAZARD_TYPES, new Map(), hazardCounts);
    hazardCounts.set(cell.item, (hazardCounts.get(cell.item) || 0) + 1);
    placedHazards.push(cell.item);
  }

  // Place Helpful Items
  // Health rate drops from 12% (level 1) to 5% (level 8); difficulty reduces it further.
  const levelHealthBase = Math.max(0.12 - (level - 1) * 0.01, 0.05);
  let helpCount = Math.floor(validItemCells.length * levelHealthBase);
  if (difficulty === 'hard') helpCount = Math.floor(validItemCells.length * Math.max(levelHealthBase - 0.03, 0.03));
  if (difficulty === 'tough_genius') helpCount = Math.floor(validItemCells.length * Math.max(levelHealthBase - 0.06, 0.02));

  const inventoryCounts = countItems(playerInventory);
  const remedyCounts = new Map<ItemTypeValues, number>();
  const guaranteedRemedies = Array.from(new Set(placedHazards))
    .map(hazard => {
      const remedies = HAZARD_REMEDIES[hazard] || [];
      if (remedies.length === 0) return undefined;
      const remedy = pickLeastRepresented(remedies, inventoryCounts, remedyCounts);
      remedyCounts.set(remedy, (remedyCounts.get(remedy) || 0) + 1);
      return remedy;
    })
    .filter((item): item is ItemTypeValues => Boolean(item));

  let placedRemedies = 0;
  for (const remedy of guaranteedRemedies) {
    const cell = pickSafeCell();
    if (!cell) break;
    cell.item = remedy;
    placedRemedies += 1;
  }

  const helpfulItems = [
    ItemType.Health, ItemType.Score, ItemType.Weapon,
    ItemType.IceCube, ItemType.CO2, ItemType.Antivenom,
    ItemType.Water, ItemType.Rope, ItemType.Shield,
    ItemType.Bandage, ItemType.Herb, ItemType.Honey, ItemType.Stick,
    ItemType.Teamwork, ItemType.ShieldGenerator, ItemType.EvasiveManeuver,
    ItemType.NegotiationItem, ItemType.RadiationShield, ItemType.OxygenTank,
    ItemType.OxygenMask, ItemType.Root, ItemType.Trap, ItemType.Shelter,
    ItemType.PharmacyPack, ItemType.Sunscreen, ItemType.LifeJacket,
    ItemType.Ventilation, ItemType.Helmet, ItemType.Money,
    ItemType.ReflexBoost, ItemType.Dodge, ItemType.HealthBerries,
    ItemType.FlowerPollen, ItemType.GameMeat, ItemType.Syringe,
    ItemType.GasMask
  ];
  const floorItemCounts = new Map(remedyCounts);

  for (let i = 0; i < Math.max(0, helpCount - placedRemedies); i++) {
    const cell = pickSafeCell();
    if (!cell) break;

    cell.item = pickLeastRepresented(helpfulItems, inventoryCounts, floorItemCounts);
    floorItemCounts.set(cell.item, (floorItemCounts.get(cell.item) || 0) + 1);
  }

  return { grid, startPos: { x: startX, y: startY } };
};
